import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';

export const ALLOWED_FILE_EXTENSIONS = ['.ifc', '.ifczip'] as const;

@Injectable()
export class FileService {
  private readonly document = inject(DOCUMENT);

  private readonly _file = signal<File | null>(null);
  readonly file = this._file.asReadonly();

  private readonly _files = signal<File[]>([]);
  readonly files = this._files.asReadonly();

  private readonly _unsupportedFile = signal(false);
  readonly unsupportedFile = this._unsupportedFile.asReadonly();

  open(accept = ALLOWED_FILE_EXTENSIONS.join(','), multiple = false): void {
    const input = this.document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    input.accept = accept;
    input.multiple = multiple;

    input.onchange = () => {
      this.onFileSelected(input, accept);
      input.remove();
    };

    this.document.body.appendChild(input);
    input.click();
  }

  onFileSelected(input: HTMLInputElement, accept = ALLOWED_FILE_EXTENSIONS.join(',')): void {
    if (input.files && input.files.length > 0) {
      this.validateAndSetFiles(Array.from(input.files), accept);
    }
  }

  setDroppedFile(file: File, accept = ALLOWED_FILE_EXTENSIONS.join(',')): void {
    this.validateAndSetFiles([file], accept);
  }

  /** Returns whether the given file has an allowed extension. */
  isSupportedFile(file: File, accept = ALLOWED_FILE_EXTENSIONS.join(',')): boolean {
    const fileName = file.name.toLowerCase();
    const allowedExtensions = this.getAllowedExtensions(accept);
    return allowedExtensions.length === 0 || allowedExtensions.some(ext => fileName.endsWith(ext));
  }

  /**
   * Validates a list of selected files. Supported files are stored in `files`
   * (and the first one in `file` for backward compatibility). If none are
   * supported, the unsupported-file flag is raised.
   */
  private validateAndSetFiles(files: File[], accept = ALLOWED_FILE_EXTENSIONS.join(',')): void {
    const supportedFiles = files.filter(file => this.isSupportedFile(file, accept));
    if (supportedFiles.length === 0) {
      this._file.set(null);
      this._files.set([]);
      this._unsupportedFile.set(true);
      return;
    }
    this._unsupportedFile.set(false);
    this._files.set(supportedFiles);
    this._file.set(supportedFiles[0]);
  }

  private getAllowedExtensions(accept: string): string[] {
    return accept
      .split(',')
      .map(part => part.trim().toLowerCase())
      .filter(part => part.startsWith('.'));
  }

  clearUnsupportedFile(): void {
    this._unsupportedFile.set(false);
  }

  clearFile(): void {
    this._file.set(null);
    this._files.set([]);
  }

  readonly unsupportedFileTypeDialogConfig = {
    showCloseButton: false,
  } as const;

  handleUnsupportedFileDialogClose(ref: BrnDialogRef | null, destroyRef: DestroyRef): void {
    if (ref) {
      ref.closed$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
        this.clearUnsupportedFile();
      });
    }
  }
}
